import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { useNavigate } from 'react-router-dom';
import { makeRequest } from '@/shared/services/http';
import { CustomersController, ICustomersLoginResponse, ICustomersGetResponse, CustomersStepTypeEnum } from '@/shared/endpoints/customers';
import { ILoginErrorResponse } from '@/shared/types/interfaces';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import './login.css';
import { ROUTES } from '@/shared/constants/routes';
import { selectLoginLoading } from '@/store/slices/auth/selectors';
import { Link } from 'react-router-dom';
import { KeyboardEventHandler, useEffect, useState } from 'react';
import { ERROR_MESSAGES } from '@/shared/constants/error-messages';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { Checkbox, Input, Button, Modal } from '@/shared/ui';
import { GtmEvent } from '@/shared/types/enums';
import { useCookies } from 'react-cookie';
import { resetOnboarding } from '../../../../store/slices/onboarding';
import VerifyCodeV2 from '../../components/register-form-v2/verify-code';
import { ICustomerVerifyCodeResponse } from '@/shared/endpoints/customers';

// Images
import LoginBg from '@/assets/images/login-bg.jpg';
import ResetPassword from './reset-password';


interface IInitialValues {
    email: string;
    pass: string;
    terms: boolean;
}

const validationSchema = Yup.object().shape({
    email: Yup.string().email(ERROR_MESSAGES.VALID_EMAIL).required(ERROR_MESSAGES.REQUIRED_FIELD_MISSING),
    pass: Yup.string().required(ERROR_MESSAGES.REQUIRED_FIELD_MISSING)
});

const initialValues: IInitialValues = {
    email: '',
    pass: '',
    terms: true
};

interface ConfirmCodeProps {
    email: string;
    terms: boolean; // Adjust type based on what 'terms' represents (boolean, string, etc.)
    otpDeliverySource: string; // Adjust the type if needed
    initialOtpTarget: string; // Adjust the type if needed
}

const Login = () => {
    const dispatch = useAppDispatch();
    const [, setCookie] = useCookies(['sessionRecording']);
    const loginLoading = useAppSelector(selectLoginLoading);
    const [isResetModalShow, setIsResetModalShown] = useState(false);
    const [verificationPayloadResultingFromLoginAttempt, setVerificationPayloadResultingFromLoginAttempt] = useState<ConfirmCodeProps | null>(null);
    const sendDataToGTM = useGTMDispatch();
    const navigate = useNavigate();

    // const posthog = usePostHog()

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema,
        onSubmit: values => {
            userLogin(values);
        }
    });

    useEffect(() => {
        window.localStorage.removeItem('takeMoment');
        dispatch(resetOnboarding());
    }, [dispatch]);

    const userLogin = async (values: IInitialValues) => {
        const { terms, ...restOfValues } = values;
        await dispatch(CustomersController.login(restOfValues)).then(async ({ meta, payload }) => {
            if (meta.requestStatus === 'rejected') {
                const { message } = payload as ILoginErrorResponse;
                if (message === ERROR_MESSAGES.NOT_VERIFIED_ACCOUNT) {
                    const { data } = payload as ILoginErrorResponse;
                    const otpDeliverySource = data?.source;
                    const initialOtpTarget = data?.channel === 'EMAIL' ? 'email' : 'phone';
                    setVerificationPayloadResultingFromLoginAttempt({
                        email: values.email,
                        terms,
                        otpDeliverySource,
                        initialOtpTarget,
                    });
                }
                return;
            }
            const { id } = payload as ICustomersLoginResponse;
            try {
                const customer = await makeRequest<ICustomersGetResponse>('get', 'customers', '');
                const currentStep = customer?.userOnboarding?.details?.currentStep;

                if (currentStep === CustomersStepTypeEnum.Done) {
                    sendDataToGTM({ event: GtmEvent.LOGIN_DASHBOARD_ALREADY, userId: id });
                }
            } catch (e) {
                console.error('Error fetching customer data:', e);
            }
            // If verified login we need to set the cookie, setVerificationPayloadResultingFromLoginAttempt, toggle start recording if terms accepted
            sendDataToGTM({ event: GtmEvent.LOGIN, userId: id, sessionRecording: terms });
            setCookie('sessionRecording', terms, { path: "/" });
        });
    };

    const onLoginCodeVerification = (res: ICustomerVerifyCodeResponse) => {
        const { id } = res;
        const { terms } = verificationPayloadResultingFromLoginAttempt as ConfirmCodeProps;
        sendDataToGTM({ event: GtmEvent.LOGIN, userId: id, sessionRecording: terms });
        setCookie('sessionRecording', terms, { path: "/" });
    }

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = e => {
        if (e.key === 'Enter') {
            formik.submitForm();
        }
    };

    return (
        <div className="flex w-full flex-col items-start lg:flex-row lg:gap-16 xl:gap-[100px]" data-testid="login-container">
            <div className="relative w-full flex-1" data-testid="login-left-block">
                <img className="w-full login__banner" src={LoginBg} alt="Medbox icon" />
                <div className="gradient-overlay absolute inset-0 hidden lg:block"></div>
                <div className="gradient-overlay-v2 absolute inset-0 block lg:hidden"></div>
            </div>

            <div className="relative z-10 -mt-8 w-full flex-1 pl-4 pr-4 lg:mt-24 lg:pl-0 lg:pr-8 md-max:pb-9" data-testid="login-right-block">
                <div className="xl:max-w-[540px]">
                    <h1 data-testid="login-title" className="font-agrandir text-4xl text-dark lg:text-50 lg:leading-[65px] lg-max:leading-[45px] login__heading">
                        Welcome Back to <span className="text-primary-700">Your MedBox</span>
                    </h1>
                    <p data-testid="login-subtitle" className="my-3 mb-4 font-atkinsonHyperlegible text-lg lg:mb-6 lg:mt-3 lg:text-xl">
                        Please log in to your existing account.
                    </p>

                    <Input
                        autoFocus
                        className="mb-6 login__email"
                        name="email"
                        label="Email"
                        value={formik.values.email}
                        required
                        placeholder="ex. janed@gmail.com"
                        error={!!(formik.touched.email && formik.errors.email)}
                        errorMessage={formik.errors.email}
                        onChange={event => formik.setFieldValue('email', event.target.value)}
                        labelClassName="font-dmSans"
                        type="text"
                        testId="login-email"
                    />

                    <Input
                        name="pass"
                        className="mb-1 lg:mb-4 login__password"
                        type="password"
                        label="Password"
                        value={formik.values.pass}
                        required
                        placeholder="•••••••••"
                        error={!!(formik.touched.pass && formik.errors.pass)}
                        errorMessage={formik.errors.pass}
                        onChange={event => formik.setFieldValue('pass', event.target.value)}
                        labelClassName="font-dmSans"
                        onKeyDown={handleKeyDown}
                        testId="login-password"
                    />
                    <Checkbox
                        testId="login-session-record"
                        uncheckedStrokeColor='#9FA4A1'
                        className={`!items-center mt-7 mb-3 !gap-5 login__terms border p-5 rounded-2xl ${formik.values.terms ? 'border-primary-400' : 'border-gray-300'
                            }`}
                        label={
                            <span data-testid="login-session-record-content" className="font-dmSans text-base xl:text-lg">
                                Your session may be recorded to improve user experience. Uncheck to Opt out.{' '}
                                <a
                                    target="_blank"
                                    className="cursor-pointer font-dmSansBold text-black underline"
                                    href="https://medbox.com/cookie-policy/"
                                    style={{ fontWeight: '700' }}
                                >
                                    Cookie Policy
                                </a>
                            </span>
                        }
                        disabled={false}
                        name="terms"
                        checked={formik.values.terms}
                        onChange={formik.handleChange}
                    />
                    <div className="mb-6 flex w-full flex-col items-start lg:mb-8">
                        <Button
                            className="mb-6 login__forgot_password"
                            variant="text"
                            sx={{ padding: 0, marginBottom: '16px' }}
                            onClick={() => setIsResetModalShown(true)}
                            testId="button-auth-forgot"
                        >
                            Forgot Password?
                        </Button>

                        <Button
                            className='login__login'
                            sx={{
                                width: '100%',
                                fontSize: 18,
                                boxShadow: '0px 4px 7.1px 0px #0000000F'
                            }}
                            loading={loginLoading}
                            showEndIcon
                            onClick={() => formik.handleSubmit()}
                            testId="button-auth-login"
                        >
                            Log In
                        </Button>
                        <Link data-testid="button-auth-create-account-link" to={ROUTES.SIGN_UP} className="mt-2.5 inline-block h-full w-full lg:mt-5 login__create_account_link">
                            <Button
                                testId="button-auth-create-account"
                                className='login__create_account_btn'
                                variant="outlined"
                                sx={{ width: '100%', fontSize: 18 }}
                                fontClassName="font-dmSansMedium"
                            >
                                Create Account
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>

            <Modal
                open={isResetModalShow}
                onClose={() => setIsResetModalShown(false)}
                contentClassName="outline-none py-[40px] sm:px-[50px] sm:max-w-[540px]"
                closeOnClickOutside={false}
                dataTestId="login-reset-password"
            >
                <ResetPassword initialEmail={formik.values.email || ''} />
            </Modal>

            {verificationPayloadResultingFromLoginAttempt &&
                <Modal
                    dataTestId="login-verify-code"
                    contentClassName="md:!max-w-[560px] outline-none sm:py-[40px] sm:px-[50px] sm:max-w-[540px]"
                    open={!!verificationPayloadResultingFromLoginAttempt} closeOnClickOutside={false} onClose={() => setVerificationPayloadResultingFromLoginAttempt(null)}>
                    <VerifyCodeV2
                        initialSourceForDelivery={verificationPayloadResultingFromLoginAttempt.otpDeliverySource}
                        email={verificationPayloadResultingFromLoginAttempt?.email}
                        onSubmit={onLoginCodeVerification}
                        customHeader="Verify your account to log&nbsp;in"
                        troubleWithModal={false}
                        initialSignUpOtpTarget={verificationPayloadResultingFromLoginAttempt.initialOtpTarget}
                    />
                </Modal>}
        </div>
    );
};

export default Login;
